import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getNoteCategories,
  createNoteRequest,
  getMyNoteRequests,
  getNoteRequests,
  getNoteRequest,
  previewNoteRequest,
  applyNoteRequest,
  linkNoteRequestListing,
  unlinkNoteRequestListing,
  resolveNoteRequest,
  cancelNoteRequest,
  searchNoteListings,
  getNoteSyncStatus,
  getNoteSyncRecentWrites,
  getNoteSyncConfig,
  updateNoteSyncConfig,
  saveNoteCategories,
  runNoteSyncTick,
  uploadNoteAttachment,
  deleteNoteAttachment,
} from '../api';
import { inventoryKeys } from './useInventory';

// Note requests: buyers ask for a public-note phrase on an order; the sweeper
// (or a pricer pressing Apply) writes it to the Skybox listing. Every read
// here is SQLite/in-memory-cache cheap on the server (0 Skybox calls), so
// polling is fine. Mutations invalidate the whole family; Apply also
// invalidates the Dashboard inventory so the patched publicNotes show up.
//
// NEVER invalidate eventPricing keys from here — the Compare pricing endpoint
// refetches Skybox on every miss and Apply already invalidates that server
// cache for the affected event.

export const noteRequestKeys = {
  all: ['noteRequests'],
  mine: (params) => ['noteRequests', 'mine', typeof params === 'string' ? { scope: params } : (params || {})],
  list: (filters) => ['noteRequests', 'list', filters || {}],
  detail: (id) => ['noteRequests', 'detail', id],
  preview: (id) => ['noteRequests', 'preview', id],
  categories: (all = false) => ['noteRequests', 'categories', all ? 'all' : 'active'],
  status: ['noteRequests', 'status'],
  config: ['noteRequests', 'config'],
  recentWrites: ['noteRequests', 'recentWrites'],
  search: (q) => ['noteRequests', 'search', q || ''],
};

// ---- Queries ------------------------------------------------------------------

// Active categories for the buyer form; `all=true` (staff) includes inactive
// ones for the admin editor.
export function useNoteCategories(all = false, options = {}) {
  return useQuery({
    queryKey: noteRequestKeys.categories(all),
    queryFn: async () => (await getNoteCategories(all)).data,
    staleTime: 5 * 60 * 1000,
    ...options,
  });
}

// Buyer's own requests — complete history ({ scope:'all'|'open', q, limit }).
// Polls so "waiting" flips to "done" on their phone without a reload; also
// carries enabled/autoApply for the buyer banner and `total` for paging.
export function useMyNoteRequests(params = { scope: 'all' }, options = {}) {
  return useQuery({
    queryKey: noteRequestKeys.mine(params),
    queryFn: async () => (await getMyNoteRequests(params)).data,
    refetchInterval: 15000,
    refetchOnWindowFocus: true,
    placeholderData: (previousData) => previousData,
    ...options,
  });
}

// Staff list for one tab (queue|waiting|done|all) with optional q filter.
// Keeps the previous rows while a search refines within the same tab, but
// not across tabs (each tab has its own columns — stale rows would misrender).
export function useNoteRequestList(filters = {}, options = {}) {
  return useQuery({
    queryKey: noteRequestKeys.list(filters),
    queryFn: async () => (await getNoteRequests(filters)).data,
    refetchInterval: 30000,
    refetchOnWindowFocus: true,
    placeholderData: (previousData, previousQuery) => {
      const prevTab = previousQuery?.queryKey?.[2]?.tab;
      return prevTab === filters?.tab ? previousData : undefined;
    },
    ...options,
  });
}

export function useNoteRequest(id, options = {}) {
  return useQuery({
    queryKey: noteRequestKeys.detail(id),
    queryFn: async () => (await getNoteRequest(id)).data,
    enabled: !!id,
    ...options,
  });
}

// Before → after per listing for the Apply modal (server composes from the
// inventory cache; 0 Skybox calls). Not cached across opens.
export function useNoteRequestPreview(id, options = {}) {
  return useQuery({
    queryKey: noteRequestKeys.preview(id),
    queryFn: async () => (await previewNoteRequest(id)).data,
    enabled: !!id,
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
    ...options,
  });
}

// Inventory-cache search for the Link listing panel (staff only — rows carry
// internal notes). Caller debounces q.
export function useNoteListingSearch(q, options = {}) {
  const query = (q || '').trim();
  return useQuery({
    queryKey: noteRequestKeys.search(query),
    queryFn: async () => (await searchNoteListings(query)).data,
    enabled: query.length >= 2,
    staleTime: 30 * 1000,
    placeholderData: (previousData) => previousData,
    ...options,
  });
}

// Sweeper state + counts + cache status (staff only endpoint)
export function useNoteSyncStatus(options = {}) {
  return useQuery({
    queryKey: noteRequestKeys.status,
    queryFn: async () => (await getNoteSyncStatus()).data,
    refetchInterval: 30000,
    refetchOnWindowFocus: true,
    retry: 1,
    ...options,
  });
}

// Admin only
export function useNoteSyncConfig(options = {}) {
  return useQuery({
    queryKey: noteRequestKeys.config,
    queryFn: async () => (await getNoteSyncConfig()).data,
    staleTime: 30 * 1000,
    ...options,
  });
}

// Staff only
export function useNoteSyncRecentWrites(options = {}) {
  return useQuery({
    queryKey: noteRequestKeys.recentWrites,
    queryFn: async () => (await getNoteSyncRecentWrites()).data,
    staleTime: 30 * 1000,
    refetchOnWindowFocus: true,
    ...options,
  });
}

// ---- Mutations ----------------------------------------------------------------

function useInvalidateAll() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: noteRequestKeys.all });
}

export function useCreateNoteRequest() {
  const invalidate = useInvalidateAll();
  return useMutation({
    mutationFn: (body) => createNoteRequest(body),
    onSuccess: invalidate,
  });
}

// Manual Apply from the staff queue. Also marks the Dashboard inventory stale
// so its Public Notes column re-reads the patched cache. (eventPricing is
// deliberately NOT touched — see header.)
export function useApplyNoteRequest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, inventoryIds }) =>
      applyNoteRequest(id, inventoryIds && inventoryIds.length ? { inventoryIds } : {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: noteRequestKeys.all });
      queryClient.invalidateQueries({ queryKey: inventoryKeys.all });
    },
  });
}

export function useLinkNoteListing() {
  const invalidate = useInvalidateAll();
  return useMutation({
    mutationFn: ({ id, inventoryId }) => linkNoteRequestListing(id, inventoryId),
    onSuccess: invalidate,
  });
}

export function useUnlinkNoteListing() {
  const invalidate = useInvalidateAll();
  return useMutation({
    mutationFn: ({ id, listingId }) => unlinkNoteRequestListing(id, listingId),
    onSuccess: invalidate,
  });
}

export function useResolveNoteRequest() {
  const invalidate = useInvalidateAll();
  return useMutation({
    mutationFn: ({ id, note }) => resolveNoteRequest(id, note),
    onSuccess: invalidate,
  });
}

export function useCancelNoteRequest() {
  const invalidate = useInvalidateAll();
  return useMutation({
    mutationFn: (id) => cancelNoteRequest(id),
    onSuccess: invalidate,
  });
}

// Photos: blob comes from utils/imagePrep (already ≤ 900 KB, EXIF stripped)
export function useUploadNoteAttachment() {
  const invalidate = useInvalidateAll();
  return useMutation({
    mutationFn: ({ id, blob, name }) => uploadNoteAttachment(id, blob, name),
    onSuccess: invalidate,
  });
}

export function useDeleteNoteAttachment() {
  const invalidate = useInvalidateAll();
  return useMutation({
    mutationFn: ({ id, attId }) => deleteNoteAttachment(id, attId),
    onSuccess: invalidate,
  });
}

// Admin: config / categories / manual tick
export function useUpdateNoteSyncConfig() {
  const invalidate = useInvalidateAll();
  return useMutation({
    mutationFn: (updates) => updateNoteSyncConfig(updates),
    // The enabled/auto_apply switches change what every list/status returns;
    // tag_options changes the buyer chips (noteRequestKeys.categories sits under .all)
    onSuccess: invalidate,
  });
}

export function useSaveNoteCategories() {
  const invalidate = useInvalidateAll();
  return useMutation({
    mutationFn: (categories) => saveNoteCategories(categories),
    onSuccess: invalidate,
  });
}

export function useRunNoteSyncTick() {
  const invalidate = useInvalidateAll();
  return useMutation({
    mutationFn: () => runNoteSyncTick(),
    onSuccess: invalidate,
  });
}
